import { useMutation, useQueryClient } from "@tanstack/react-query";
import { v2Api } from "@/lib/v2-api";
import { usePreOrderMergeMutation, type MergeOutcome } from "@stockline/web";
import { preOrderKeys } from "./keys";
import { partnerKeys } from "../../keys/partners";

export interface BatchMergeArgs {
  sourceIds: string[];
}

export interface BatchMergeResult {
  mergedId: string;
  outcome: MergeOutcome;
}

/**
 * Offline-first batch-merge. The local Dexie write happens in
 * `mergePreOrders` BEFORE the network call so `useLiveQuery`-driven recap
 * re-renders within one render cycle of the click. The V2 POST carries
 * the same UUIDs so SSE arrives idempotently. On error we inverse-write
 * the optimistic state before re-throwing so the caller's toast / dialog
 * code runs against a clean store.
 *
 * Cache invalidation is intentionally narrow now that the recap reads
 * from Dexie: the old all-of-pre-orders cascade thrashed 2k+ cached
 * queries for views that no longer source from TanStack.
 */
export function useBatchMergePreOrders() {
  const queryClient = useQueryClient();
  const { mergePreOrders, rollbackMerge } = usePreOrderMergeMutation();

  return useMutation<BatchMergeResult, unknown, BatchMergeArgs>({
    mutationFn: async ({ sourceIds }) => {
      const outcome = await mergePreOrders(sourceIds);
      try {
        await v2Api.preOrders.batchMerge(sourceIds, {
          mergedId: outcome.mergedId,
          flowIdMapping: outcome.clonedFlows,
        });
      } catch (err) {
        // Inverse-write the optimistic Dexie state before re-throwing so
        // the caller's toast/dialog code runs against a clean store.
        // Swallow rollback errors — server-side state is authoritative; the
        // next SSE event or full resync will reconcile any orphans.
        await rollbackMerge(outcome).catch(() => {});
        throw err;
      }
      return { mergedId: outcome.mergedId, outcome };
    },
    onSuccess: () => {
      // Partner order-history caches still source from TanStack — invalidate
      // them so the merged PO replaces the sources in the history widget.
      queryClient.invalidateQueries({
        queryKey: [...partnerKeys.all, "order-history"],
      });
      // V2 sync wrapper caches keyed under [...all, "v2", ...] — light
      // invalidation for the few non-Dexie consumers (e.g. legacy
      // email-page lists) that still pull from TanStack.
      queryClient.invalidateQueries({
        queryKey: [...preOrderKeys.all, "v2"],
      });
      // Legacy chat-side badges (PreOrderBadgeWrapper / -SMS) still pull
      // the per-chat pre-order list from TanStack via the byChat/bySMSChat
      // keys — invalidate so they drop the now-tombstoned sources and
      // surface the merged row after a batch-merge.
      queryClient.invalidateQueries({
        queryKey: [...preOrderKeys.all, "chat"],
      });
      queryClient.invalidateQueries({
        queryKey: [...preOrderKeys.all, "sms-chat"],
      });
    },
  });
}
